import {useFormContext} from 'react-hook-form';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@common-web/components/ui/form';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@common-web/components/ui/tooltip';
import {Info} from 'lucide-react';
import {ALLOWED_WAITING_PERIOD_DAYS} from '@common/lib/rfcSchema';

export function Section125Admin({showEnrollmentType = false}: {showEnrollmentType?: boolean}) {
  const {control} = useFormContext();

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div className='space-y-2'>
          <div className='font-semibold'>Who Is Eligible for Coverage? *</div>
          <div className='flex items-center gap-6'>
            <FormField
              control={control}
              name={'section125Form.admin.eligibility.fullTime'}
              render={({field}) => (
                <FormItem className='flex items-center gap-2'>
                  <FormControl>
                    <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel className='m-0'>Full Time Employees</FormLabel>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={'section125Form.admin.eligibility.partTime'}
              render={({field}) => (
                <FormItem className='flex items-center gap-2'>
                  <FormControl>
                    <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel className='m-0'>Part Time Employees</FormLabel>
                </FormItem>
              )}
            />
          </div>
        </div>
        <FormField
          control={control}
          name={'section125Form.admin.waitingPeriod'}
          render={({field}) => (
            <FormItem>
              <FormLabel className='flex items-center gap-1.5'>
                Waiting period for newly hired employees (Optional)
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        role='button'
                        tabIndex={-1}
                        aria-label='Waiting period help'
                        className='inline-flex items-center text-brand-gray hover:text-brand-navy'
                        onClick={(e) => e.preventDefault()}
                      >
                        <Info className='h-4 w-4' />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side='top' className='max-w-xs'>
                      This is how long after a new employee's hire date benefits can begin.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </FormLabel>
              <Select
                onValueChange={(value) => field.onChange(value === 'NONE' ? null : Number(value))}
                // null is an explicit "No waiting period" choice; only undefined/'' show the placeholder.
                value={
                  field.value === null
                    ? 'NONE'
                    : field.value !== undefined && field.value !== ''
                      ? String(field.value)
                      : undefined
                }
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder='Select waiting period' />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value='NONE'>No waiting period</SelectItem>
                  {ALLOWED_WAITING_PERIOD_DAYS.map((days) => (
                    <SelectItem key={days} value={String(days)}>
                      {days} days
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={'section125Form.admin.payrollFrequencies'}
          render={({field}) => (
            <FormItem>
              <FormLabel>Payroll Frequencies *</FormLabel>
              <div className='space-y-2'>
                {[
                  {value: 'WEEKLY', label: 'Weekly'},
                  {value: 'WEEKLY_52', label: 'Weekly (52)'},
                  {value: 'WEEKLY_48', label: 'Weekly (48)'},
                  {value: 'BI_WEEKLY', label: 'Bi-Weekly'},
                  {value: 'BI_MONTHLY', label: 'Bi-Monthly'},
                  {value: 'MONTHLY', label: 'Monthly'},
                  {value: 'SEMI_MONTHLY', label: 'Semi-Monthly'},
                ].map((option) => (
                  <div key={option.value} className='flex items-center gap-2'>
                    <Checkbox
                      checked={field.value?.includes(option.value)}
                      onCheckedChange={(checked) => {
                        const currentValues = field.value || [];
                        if (checked) {
                          field.onChange([...currentValues, option.value]);
                        } else {
                          field.onChange(currentValues.filter((v: string) => v !== option.value));
                        }
                      }}
                    />
                    <label className='text-sm font-normal'>{option.label}</label>
                  </div>
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        {showEnrollmentType && (
          <FormField
            control={control}
            name={'section125Form.admin.enrollmentType'}
            render={({field}) => (
              <FormItem>
                <FormLabel>Enrollment Type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder='Select enrollment type' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value='AUTO_ENROLL'>Auto Enrollment</SelectItem>
                    <SelectItem value='VOLUNTARY'>Voluntary Enrollment</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );
}
